import { generateClient } from 'aws-amplify/api';
import { signOut } from 'aws-amplify/auth';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import type { Subscription } from 'rxjs';
import type { driverIndividualNotification } from '@/API';
import { ModelSortDirection } from '@/API';
import ErrorDialog from '@/components/shared/errorDialog';
import BellIconSvg from '@/components/shared/image/icon_bell_menu.svg';
import CarIconSvg from '@/components/shared/image/icon_car_menu.svg';
import { MobiTitle } from '@/components/shared/MobiTitle';
import { ApiNamePaths } from '@/constants/rest-api-name-path';
import StorageKeys from '@/constants/storageKeys';
import * as queries from '@/graphql/queries';
import * as subscriptions from '@/graphql/subscriptions';
import { apiDelete } from '@/modules/hooks/use-delete';
import { useServiceType } from '@/modules/hooks/useServiceType';
import * as paths from '@/routes/paths';
import { settingState } from '@/state/settingState';
import { ServiceType } from '@/types/serviceType';

type StyledBadgeProps = {
  badgeContent: number;
  children: React.ReactNode;
};

export const StyledBadge: React.FC<StyledBadgeProps> = ({ badgeContent, children }) => (
  <span className="badge-wrapper">
    {children}
    {badgeContent > 0 && <span className="badge-counter">{badgeContent}</span>}
  </span>
);

const ChevronRightIcon: React.FC = () => (
  <svg className="nav-icon chevron-icon" viewBox="0 0 28 28" aria-hidden="true">
    <path d="M10 6L18 14L10 22" />
  </svg>
);

const LogoutIcon: React.FC = () => (
  <svg className="nav-icon item-icon" viewBox="0 0 32 32" aria-hidden="true">
    <path d="M14 6H9C7.9 6 7 6.9 7 8V24C7 25.1 7.9 26 9 26H14" />
    <path d="M18 11L24 16L18 21" />
    <path d="M12 16H24" />
  </svg>
);

// ドライバ個別通知一覧取得APIから通知一覧情報を取得する
export async function fetchNotifications(
  driverId: string,
  retries: number = 3,
): Promise<[driverIndividualNotification[], boolean]> {
  let isError = false;

  try {
    const client = generateClient();
    const variables = {
      createdAt: { lt: new Date().toDateString() },
      driverId,
      sortDirection: ModelSortDirection.DESC,
    };

    const driverIndividualNoticeData = await client.graphql({
      query: queries.listDriverIndividualNotifications,
      variables,
    });
    const driverIndividualNotificationsList =
      driverIndividualNoticeData?.data?.listDriverIndividualNotifications?.items;

    return [driverIndividualNotificationsList, true];
  } catch (_error) {
    isError = true;
  }

  if (isError && retries > 0) {
    return fetchNotifications(driverId, retries - 1);
  }

  return [[], false];
}

export const MenuPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [setting, setSetting] = useRecoilState(settingState);
  const [logOutDisabled, setLogOutDisabled] = useState(false);

  const [unreadCount, setUnreadCount] = useState(0);
  const [driverIndividualNotificationsInfo, setDriverIndividualNotificationsInfo] = useState<
    driverIndividualNotification[]
  >([]);
  const [isSubApiSuccess, setIsSubApiSuccess] = useState(false);
  const [isListApiSuccess, setIsListApiSuccess] = useState(false);
  const [onlineOfflineStatus, setOnlineOfflineStatus] = useState(-1); // -1：デフォルト値（初期化とする）、０：オフライン、１：再オンライン

  // エラーダイアログ
  const [modalShow, setModalShow] = useState<boolean>(false);
  const [vehicleDriverDeleteError, setVehicleDriverDeleteError] = useState('');

  // ドライバ管理会社ID
  const serviceType = useServiceType();

  // ドライバID（cognitoのdriverId）
  const driverId = setting?.driverId || '';

  useEffect(() => {
    const handleOnline = () => {
      // １：再オンライン
      setOnlineOfflineStatus(1);
    };

    const handleOffline = () => {
      // ０：オフライン
      setOnlineOfflineStatus(0);
      // ドライバ個別通知購読API失敗
      setIsSubApiSuccess(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    // 画面初期処理
    const getUnreadCount = async () => {
      const [responseDriverIndividualNotifications, isCallApiSuccess] = await fetchNotifications(driverId);
      // ドライバ個別通知一覧取得API成功フラグを設定する
      setIsListApiSuccess(isCallApiSuccess);

      if (!responseDriverIndividualNotifications || responseDriverIndividualNotifications.length === 0) {
        setDriverIndividualNotificationsInfo([]);
        setUnreadCount(0);

        return;
      }

      setDriverIndividualNotificationsInfo(responseDriverIndividualNotifications);

      const unreadNotifications = responseDriverIndividualNotifications.filter((item) => !item?.isRead);
      setUnreadCount(unreadNotifications.length);
    };

    // オフライン以外の場合（初期処理と再オンライン）、
    if (onlineOfflineStatus !== 0) {
      // ドライバ個別通知購読APIを実行後にドライバ個別通知一覧取得APIを実行する。
      if (isSubApiSuccess) {
        void getUnreadCount();
      }
    }
  }, [driverId, isSubApiSuccess, onlineOfflineStatus]);

  // リアルタイム
  useEffect(() => {
    const subscription: Subscription[] = [];
    const subscribeWithRetry = (retryCount = 3) => {
      if (retryCount >= 0) {
        const client = generateClient();
        const variables = {
          createdAt: { lt: new Date().toDateString() },
          driverId,
          filter: {
            isRead: { eq: false },
            driverId: { eq: driverId },
          },
        };

        const createSub = client
          .graphql({ query: subscriptions.onCreateDriverIndividualNotification, variables })
          .subscribe({
            next: () => {
              setUnreadCount((prevCount) => prevCount + 1);
            },
            error: () => {
              // ドライバ個別通知購読API失敗
              setIsSubApiSuccess(false);
              createSub.unsubscribe();
              setTimeout(() => subscribeWithRetry(retryCount - 1), 2000);
            },
          });

        if (createSub && !createSub.closed) {
          // ドライバ個別通知購読API成功
          setIsSubApiSuccess(true);
        }

        subscription.push(createSub);
      }
    };

    // オフライン以外の場合（初期処理と再オンライン）、
    if (onlineOfflineStatus !== 0) {
      subscribeWithRetry(3);
    }

    return () => {
      if (subscription && subscription.length >= 0) {
        subscription.forEach((element) => {
          !element.closed && element.unsubscribe();
        });
      }
    };
  }, [driverId, onlineOfflineStatus]);

  // Amplifyのsignout関数を利用
  const commonLogOut = () => {
    signOut()
      .then(async () => {
        setSetting(undefined);
        navigate(paths.login);
      })
      .catch(() => {});
  };

  const logOut = async () => {
    // 定時定路線の場合
    if (serviceType === ServiceType.FixedRouteSchedule) {
      // ドライバIDをを渡して、車両ドライバ削除APIをリクエストする
      await apiDelete(ApiNamePaths.vehicleDriverApi.apiName, ApiNamePaths.vehicleDriverApi.apiPath, {
        queryParams: { driverId },
      })
        .then(() => {
          // 削除成功の場合、
          setModalShow(false);
          // セッションストレージから車両IDを削除する
          sessionStorage.removeItem(StorageKeys.VEHICLE_ID);
          // 正常終了後、ログイン画面へ遷移する
          commonLogOut();
        })
        .catch((error) => {
          // 削除失敗の場合、エラーを表示
          if (error instanceof Error) {
            setVehicleDriverDeleteError(error.message);
          } else {
            setVehicleDriverDeleteError('An unknown error occurred');
          }
          setModalShow(true);
        });
    }
    if (serviceType === ServiceType.PreBooking) {
      // セッションストレージから車両IDを削除する
      sessionStorage.removeItem(StorageKeys.VEHICLE_ID);
      // 事前予約の場合
      commonLogOut();
    }
    // リアルタイムオンデマンドの場合
    if (serviceType === ServiceType.RealtimeOnDemand) {
      // ドライバIDをを渡して、車両ドライバ削除APIをリクエストする
      await apiDelete(ApiNamePaths.vehicleDriverApi.apiName, ApiNamePaths.vehicleDriverApi.apiPath, {
        queryParams: { driverId },
      })
        .then(() => {
          // 削除成功の場合、
          setModalShow(false);
          // セッションストレージから車両IDを削除する
          sessionStorage.removeItem(StorageKeys.VEHICLE_ID);
          // 正常終了後、ログイン画面へ遷移する
          commonLogOut();
        })
        .catch((error) => {
          // 削除失敗の場合、エラーを表示
          if (error instanceof Error) {
            setVehicleDriverDeleteError(error.message);
          } else {
            setVehicleDriverDeleteError('An unknown error occurred');
          }
          setModalShow(true);
        });
    }
  };

  const itemData = [
    {
      key: 'notification',
      name: t('menu.notification'),
      icon: <img src={BellIconSvg} className="nav-icon item-icon" alt="" />,
      path: paths.notification,
      hasBadge: true,
    },
    {
      key: 'vehicle-update',
      name: t('menu.vehicleDriverUpdate'),
      icon: <img src={CarIconSvg} className="nav-icon item-icon" alt="" />,
      path: paths.vehicleDriverUpdate,
    },
  ];

  return (
    <div className="surface-root">
      <MobiTitle>{t('menu.title')}</MobiTitle>
      <div className="content-shell">
        <div className="entry-list">
          {itemData.map((item) => (
            <button
              key={item.key}
              className="entry-button"
              type="button"
              onClick={() => {
                if (!item.path) {
                  return;
                }

                if (item.path === paths.notification) {
                  navigate(item.path, {
                    state: {
                      notificationsInfo: driverIndividualNotificationsInfo,
                      isSubApiSuccess,
                      isListApiSuccess,
                    },
                  });

                  return;
                }

                navigate(item.path);
              }}
            >
              <span className="entry-left">
                {item.icon}
                <span className="entry-text">{item.name}</span>
              </span>
              <span className="entry-right">
                {item.hasBadge ? (
                  <StyledBadge badgeContent={unreadCount}>
                    <span className="badge-anchor" />
                  </StyledBadge>
                ) : undefined}
                <ChevronRightIcon />
              </span>
            </button>
          ))}
        </div>

        <div className="separator-line" />

        <button
          className="entry-button is-secondary"
          type="button"
          disabled={logOutDisabled}
          onClick={() => {
            setLogOutDisabled(true);
            void logOut();
          }}
        >
          <span className="entry-left">
            <LogoutIcon />
            <span className="entry-text text-subtle">{t('header.logout')}</span>
          </span>
          <ChevronRightIcon />
        </button>
      </div>

      {modalShow && (
        <ErrorDialog
          open={modalShow}
          errorMessage={vehicleDriverDeleteError}
          onClose={(flag) => {
            setModalShow(flag);
          }}
        />
      )}
    </div>
  );
};
